import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Loader2, Upload, ClipboardList } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { CompanyAnalysisService } from '../../../../services/api/companyAnalysisService';
import { useAppSelector } from '../../../../stores';
import {
    getInsightsCollectionLinks,
    displayInaccessibleCollectionLinks,
    listRecentCollectCollections,
    canManageDataCollectionSummary,
    type InsightsInaccessibleCollectionLink,
    type RecentCollectCollectionSummary,
} from '../../../../services/api/collectDataService';

import { iMap } from '../../../../components/type';
import {
    type CollectCollectionLinkSummary,
    type SelectedCollectCollection,
    collectionsLinkedToMapIds,
    collectionSummaryHasResponses,
    collectionSummaryManageableByOwner,
    collectionSummaryInCompany,
    mapIdsFromCollections,
    mergeMapsById,
    resolveMapIdsForProcess,
} from '../../../../utils/analysisContextLinks';

type MapRow = iMap & { id?: string };

function resolveMapId(map: MapRow): string {
    return String(map._id ?? map.id ?? '').trim();
}

function normalizeMapList(raw: unknown): iMap[] {
    if (!Array.isArray(raw)) return [];
    return raw
        .map((row: MapRow) => {
            const _id = resolveMapId(row);
            if (!_id) return null;
            return { ...row, _id };
        })
        .filter((row): row is iMap => row != null);
}

interface AnalysisModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: (options: {
        file?: File;
        files?: File[];
        maps?: iMap[];
        collections?: SelectedCollectCollection[];
    }) => void;
    selectedFile: File | null;
    selectedMapIds: string[];
    selectedMaps?: iMap[];
    selectedCollectionIds?: string[];
    selectedCollections?: SelectedCollectCollection[];
    swimlanes: any[];
    fileError: string | null;
    handleFileSelect: (file: File | null) => void;
    handleMapSelect: (maps: iMap[] | null) => void;
    handleCollectionSelect?: (collections: SelectedCollectCollection[] | null) => void;
    clearSelection: () => void;
    /** Insights = périmètre complet ; collectContext = fichier + cartographies uniquement (Collect Data setup) */
    mode?: 'insights' | 'collectContext';
}

type ModalView = 'main' | 'maps' | 'surveys';

const AnalysisFileModal: React.FC<AnalysisModalProps> = ({
    isOpen,
    onClose,
    onConfirm,
    selectedFile,
    selectedMapIds,
    selectedMaps = [],
    selectedCollectionIds = [],
    selectedCollections = [],
    fileError,
    handleFileSelect,
    handleMapSelect,
    handleCollectionSelect,
    mode = 'insights',
}) => {
    const { t } = useTranslation();
    const users = useAppSelector((state) => state.auth);
    const companyId = users.session.company_id;
    const isCollectContext = mode === 'collectContext';
    const fileInputId = isCollectContext ? 'collect-context-file-input' : 'analysis-file-input';
    /** État sidebar au moment de l'ouverture — restauré par Annuler */
    const sidebarSnapshotRef = useRef<{ maps: iMap[]; collections: SelectedCollectCollection[] }>({
        maps: [],
        collections: [],
    });
    const wasOpenRef = useRef(false);
    const [view, setView] = useState<ModalView>('main');
    const [tempFile, setTempFile] = useState<File | null>(selectedFile);
    const [tempMapIds, setTempMapIds] = useState<string[]>(selectedMapIds);
    const [tempCollectionIds, setTempCollectionIds] = useState<string[]>(selectedCollectionIds);
    const [localFileError, setLocalFileError] = useState<string | null>(fileError);
    const [swimlaneList, setSwimlaneList] = useState<iMap[]>([]);
    const [collectionList, setCollectionList] = useState<CollectCollectionLinkSummary[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [isLoadingMap, setIsLoadingMap] = useState(true);
    const [isLoadingCollections, setIsLoadingCollections] = useState(true);
    const [collectionLinkError, setCollectionLinkError] = useState<string | null>(null);
    const [inaccessibleLinks, setInaccessibleLinks] = useState<InsightsInaccessibleCollectionLink[]>([]);

    const fetchSwimlanes = useCallback(async () => {
        try {
            setIsLoadingMap(true);
            const rows = await CompanyAnalysisService.listMappingSummaries(companyId, users.access_token);
            setSwimlaneList(normalizeMapList(rows));
        } finally {
            setIsLoadingMap(false);
        }
    }, [companyId, users.access_token]);

    const fetchCollections = useCallback(async () => {
        if (!users.access_token) return;
        try {
            setIsLoadingCollections(true);
            const res = await listRecentCollectCollections(users.access_token, 50);
            const rows = res.body?.data?.collections;
            if (res.status === 200 && Array.isArray(rows)) {
                setCollectionList(
                    rows
                        .filter((r: RecentCollectCollectionSummary) =>
                            canManageDataCollectionSummary(r)
                            && collectionSummaryInCompany(r, companyId),
                        )
                        .map((r: RecentCollectCollectionSummary) => ({
                            id: r.id,
                            name: r.name,
                            company_id: r.company_id ?? null,
                            map_ids: Array.isArray(r.map_ids) ? r.map_ids : [],
                            linked_process_id: r.linked_process_id ?? null,
                            has_responses: r.has_responses === true,
                            opened_as_contributor: r.opened_as_contributor,
                            is_primary_owner: r.is_primary_owner,
                            is_collection_co_owner: r.is_collection_co_owner,
                        })),
                );
            }
        } finally {
            setIsLoadingCollections(false);
        }
    }, [users.access_token, companyId]);

    useEffect(() => {
        if (!isOpen) {
            wasOpenRef.current = false;
            return;
        }
        const justOpened = !wasOpenRef.current;
        wasOpenRef.current = true;
        if (!justOpened) return;

        sidebarSnapshotRef.current = {
            maps: [...selectedMaps],
            collections: [...selectedCollections],
        };
        setTempFile(selectedFile);
        setTempMapIds([...selectedMapIds]);
        setTempCollectionIds(isCollectContext ? [] : [...selectedCollectionIds]);
        setLocalFileError(fileError);
        setView('main');
        setSearchTerm('');
        setCollectionLinkError(null);
        setInaccessibleLinks([]);
        void Promise.all([
            fetchSwimlanes(),
            !isCollectContext ? fetchCollections() : Promise.resolve(),
            !isCollectContext && selectedMapIds.length && users.access_token
                ? getInsightsCollectionLinks(selectedMapIds, users.access_token).then((links) => {
                    setInaccessibleLinks(displayInaccessibleCollectionLinks(links));
                })
                : Promise.resolve(),
        ]);
        if (isCollectContext) {
            setCollectionList([]);
            setIsLoadingCollections(false);
        }
    }, [isOpen, isCollectContext, selectedFile, selectedMapIds, selectedMaps, selectedCollectionIds, selectedCollections, fileError, fetchSwimlanes, fetchCollections]);

    const buildCollectionsFromIds = (collectionIds: string[]): SelectedCollectCollection[] =>
        collectionList
            .filter(c => collectionIds.includes(c.id) && collectionSummaryManageableByOwner(c) && collectionSummaryHasResponses(c))
            .map(c => ({
                id: c.id,
                name: c.name,
                map_ids: [...(c.map_ids || [])],
                linked_process_id: c.linked_process_id ?? null,
            }));

    const handleCancel = () => {
        if (!isCollectContext) {
            const { maps, collections } = sidebarSnapshotRef.current;
            handleMapSelect(maps.length ? maps : null);
            if (handleCollectionSelect) {
                handleCollectionSelect(collections.length ? collections : null);
            }
        }
        setView('main');
        onClose();
    };

    const syncMapsFromCollections = (collectionIds: string[]) => {
        const selected = collectionList.filter(c => collectionIds.includes(c.id));
        const linkedMapIds = new Set(mapIdsFromCollections(selected));
        for (const c of selected) {
            if (!(c.map_ids?.length) && c.linked_process_id) {
                for (const pid of resolveMapIdsForProcess(swimlaneList, c.linked_process_id)) {
                    linkedMapIds.add(pid);
                }
            }
        }
        if (!linkedMapIds.size) return;
        setTempMapIds(prev => [...new Set([...prev, ...linkedMapIds])]);
    };

    const syncCollectionsFromMaps = async (mapIds: string[]) => {
        if (!mapIds.length || !users.access_token || isCollectContext) {
            setInaccessibleLinks([]);
            return;
        }

        const links = await getInsightsCollectionLinks(mapIds, users.access_token);
        setInaccessibleLinks(displayInaccessibleCollectionLinks(links));

        const autoIds = links.manageable
            .filter((c) => c.has_responses)
            .map((c) => c.id);
        if (!autoIds.length) return;
        setTempCollectionIds((prev) => [...new Set([...prev, ...autoIds])]);
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setLocalFileError(null);
        if (e.target.files && e.target.files.length > 0) {
            const files = Array.from(e.target.files);
            if (files.length > 0) setTempFile(files[0]);
        }
    };

    const handleToggleMapId = (mapId: string) => {
        if (!mapId) return;
        const updatedMapIds = tempMapIds.includes(mapId)
            ? tempMapIds.filter(id => id !== mapId)
            : [...tempMapIds, mapId];
        setTempMapIds(updatedMapIds);
        if (!isCollectContext) {
            void syncCollectionsFromMaps(updatedMapIds);
        }
    };

    const handleToggleCollectionId = (collectionId: string) => {
        const coll = collectionList.find(c => c.id === collectionId);
        const adding = !tempCollectionIds.includes(collectionId);
        if (adding && coll && !collectionSummaryHasResponses(coll)) {
            setCollectionLinkError(t('features.swimlane.collectLinkNeedsResponses'));
            return;
        }
        setCollectionLinkError(null);
        const updatedCollectionIds = adding
            ? [...tempCollectionIds, collectionId]
            : tempCollectionIds.filter(id => id !== collectionId);
        setTempCollectionIds(updatedCollectionIds);
        if (adding) {
            syncMapsFromCollections(updatedCollectionIds);
        }
    };

    const handleConfirm = () => {
        let selectedMaps = swimlaneList.filter(map => tempMapIds.includes(resolveMapId(map)));
        const selectedCollections = buildCollectionsFromIds(tempCollectionIds);

        const extraMapIds = mapIdsFromCollections(selectedCollections).filter(id => !tempMapIds.includes(id));
        for (const c of selectedCollections) {
            if (!(c.map_ids?.length) && c.linked_process_id) {
                for (const pid of resolveMapIdsForProcess(swimlaneList, c.linked_process_id)) {
                    if (!extraMapIds.includes(pid) && !tempMapIds.includes(pid)) {
                        extraMapIds.push(pid);
                    }
                }
            }
        }
        if (extraMapIds.length) {
            const extraMaps = swimlaneList.filter(m => extraMapIds.includes(resolveMapId(m)));
            selectedMaps = mergeMapsById(selectedMaps, extraMaps);
        }

        const fileInput = document.getElementById(fileInputId) as HTMLInputElement;
        const files = fileInput?.files ? Array.from(fileInput.files) : [];

        onConfirm({
            file: tempFile || undefined,
            files: files.length > 0 ? files : undefined,
            maps: selectedMaps.length > 0 ? selectedMaps : undefined,
            collections:
                !isCollectContext && selectedCollections.length > 0 ? selectedCollections : undefined,
        });
        onClose();
    };

    const filteredSwimlanes = swimlaneList.filter((map) => {
        const searchLower = searchTerm.toLowerCase();
        const processTitle =
            typeof map.process === 'object' && map.process !== null
                ? String((map.process as { title?: string }).title ?? '').toLowerCase()
                : '';
        const mapName = (map.name || '').toLowerCase();
        return processTitle.includes(searchLower) || mapName.includes(searchLower);
    });

    const filteredCollections = collectionList.filter((c) => {
        const q = searchTerm.toLowerCase();
        return (c.name || '').toLowerCase().includes(q);
    });

    if (!isOpen) return null;

    const canConfirm = Boolean(tempFile || tempMapIds.length > 0 || (!isCollectContext && tempCollectionIds.length > 0));

    const modalTitle = isCollectContext
        ? t('form.collectWorkspace.addContextModalTitle', 'Add context')
        : t('features.swimlane.scope');

    const collectFileAccept = '.pdf,.txt,.csv,.md,.json,application/pdf,text/plain,text/csv,text/markdown,application/json';
    const insightsFileAccept = '.pdf,.doc,.docx,.txt,.csv,.json,.geojson';

    const modalBody = (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center" style={{ zIndex: 10050 }}>
            <div className={`analysis-scope-modal bg-white rounded-2xl p-6 w-full ${isCollectContext ? 'max-w-lg' : 'max-w-md'} mx-auto shadow-xl text-gray-800`}>
                <h2 className="text-2xl font-medium text-gray-800 mb-2 text-center">
                    {modalTitle}
                </h2>
                {isCollectContext ? (
                    <p className="text-sm text-gray-600 text-center mb-6">
                        {t(
                            'form.collectWorkspace.addContextModalHint',
                            'Attach a document and/or link process maps to enrich the interview script.',
                        )}
                    </p>
                ) : (
                    <div className="mb-4" aria-hidden />
                )}

                {view === 'main' ? (
                    <div className={`grid grid-cols-1 ${isCollectContext ? 'sm:grid-cols-2' : 'sm:grid-cols-3'} gap-3 mb-6`}>
                        <div
                            className={`border rounded-lg px-3 flex flex-col items-center justify-center h-32 cursor-pointer transition-all ${tempFile ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}
                            onClick={() => document.getElementById(fileInputId)?.click()}
                        >
                            <Upload size={22} className="mb-2 text-gray-600" />
                            <p className="text-center font-medium text-gray-700 text-sm">
                                {t('features.swimlane.uploadFile')}
                            </p>
                            <input
                                type="file"
                                id={fileInputId}
                                className="hidden"
                                multiple={!isCollectContext}
                                onChange={handleFileChange}
                                accept={isCollectContext ? collectFileAccept : insightsFileAccept}
                            />
                            {tempFile && (
                                <p className="text-xs text-gray-500 mt-1 truncate max-w-full">{tempFile.name}</p>
                            )}
                        </div>

                        <div
                            className={`border rounded-lg p-3 flex flex-col items-center justify-center h-32 cursor-pointer transition-all ${tempMapIds.length > 0 ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}
                            onClick={() => { setView('maps'); setSearchTerm(''); }}
                        >
                            <i className="fa-solid fa-diagram-project mb-2 text-gray-600" />
                            <p className="text-center font-medium text-gray-700 text-sm">
                                {t('features.swimlane.selectMap')}
                            </p>
                            {tempMapIds.length > 0 && (
                                <p className="text-xs text-gray-500 mt-1">
                                    {tempMapIds.length} {tempMapIds.length > 1 ? t('words.selecteds') : t('words.selected')}
                                </p>
                            )}
                        </div>

                        {!isCollectContext ? (
                        <div
                            className={`border rounded-lg p-3 flex flex-col items-center justify-center h-32 cursor-pointer transition-all ${tempCollectionIds.length > 0 ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}
                            onClick={() => { setView('surveys'); setSearchTerm(''); }}
                        >
                            <ClipboardList size={22} className="mb-2 text-gray-600" />
                            <p className="text-center font-medium text-gray-700 text-sm">
                                {t('features.swimlane.selectDataCollection')}
                            </p>
                            {tempCollectionIds.length > 0 && (
                                <p className="text-xs text-gray-500 mt-1">
                                    {tempCollectionIds.length} {tempCollectionIds.length > 1 ? t('words.selecteds') : t('words.selected')}
                                </p>
                            )}
                        </div>
                        ) : null}
                    </div>
                ) : view === 'maps' ? (
                    <div className="mb-6">
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="text-lg font-medium text-gray-800">{t('features.swimlane.selectMap')}</h3>
                            <button type="button" onClick={() => setView('main')} className="text-sm text-blue-600 hover:text-blue-800">
                                {t('words.back')}
                            </button>
                        </div>
                        <input
                            type="text"
                            placeholder={`${t('navigation.searchAmap')}...`}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full px-3 py-2 mb-3 border border-gray-300 rounded-md bg-white text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        {inaccessibleLinks.length > 0 && (
                            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mb-3">
                                {t('companyAnalysis.insightsLinkedCollectionInaccessible', {
                                    names: inaccessibleLinks.map((l) => l.name).join(', '),
                                })}
                            </p>
                        )}
                        <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-md shadow-sm">
                            {!isLoadingMap && filteredSwimlanes.length === 0 ? (
                                <div className="px-3 py-2 text-center text-gray-500">{t('navigation.noData')}</div>
                            ) : (
                                filteredSwimlanes.map((map) => {
                                    const mapId = resolveMapId(map);
                                    const isSelected = tempMapIds.includes(mapId);
                                    const processTitle =
                                        typeof map.process === 'object' && map.process !== null
                                            ? String((map.process as { title?: string }).title ?? '')
                                            : '';
                                    return (
                                        <div
                                            key={mapId}
                                            onClick={() => handleToggleMapId(mapId)}
                                            className={`px-3 py-2 flex justify-between items-center cursor-pointer ${isSelected ? 'bg-blue-50 border-l-4 border-blue-500' : 'hover:bg-gray-50'}`}
                                        >
                                            <div className="flex flex-col">
                                                <span className={`text-sm font-medium ${isSelected ? 'text-blue-700' : 'text-gray-800'}`}>{map.name || processTitle || mapId}</span>
                                                {processTitle && map.name ? (
                                                    <span className="text-xs text-gray-500">{processTitle}</span>
                                                ) : null}
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                            {isLoadingMap && (
                                <div className="flex justify-center py-8">
                                    <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
                                </div>
                            )}
                        </div>
                    </div>
                ) : (
                    <div className="mb-6">
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="text-lg font-medium text-gray-800">{t('features.swimlane.selectDataCollection')}</h3>
                            <button type="button" onClick={() => setView('main')} className="text-sm text-blue-600 hover:text-blue-800">
                                {t('words.back')}
                            </button>
                        </div>
                        <input
                            type="text"
                            placeholder={`${t('words.search')}...`}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full px-3 py-2 mb-3 border border-gray-300 rounded-md bg-white text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        {inaccessibleLinks.length > 0 && (
                            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mb-3">
                                {t('companyAnalysis.insightsLinkedCollectionInaccessible', {
                                    names: inaccessibleLinks.map((l) => l.name).join(', '),
                                })}
                            </p>
                        )}
                        <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-md shadow-sm">
                            {!isLoadingCollections && filteredCollections.length === 0 ? (
                                <div className="px-3 py-2 text-center text-gray-500">{t('navigation.noData')}</div>
                            ) : (
                                filteredCollections.map((c) => {
                                    const isSelected = tempCollectionIds.includes(c.id);
                                    const canLink = collectionSummaryHasResponses(c);
                                    return (
                                        <div
                                            key={c.id}
                                            onClick={() => canLink && handleToggleCollectionId(c.id)}
                                            className={`px-3 py-2 flex justify-between items-center ${canLink ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'} ${isSelected ? 'bg-blue-50 border-l-4 border-blue-500' : canLink ? 'hover:bg-gray-50' : ''}`}
                                        >
                                            <span className={`text-sm font-medium ${isSelected ? 'text-blue-700' : 'text-gray-800'}`}>{c.name}</span>
                                        </div>
                                    );
                                })
                            )}
                            {isLoadingCollections && (
                                <div className="flex justify-center py-8">
                                    <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {localFileError && <p className="text-xs text-red-500 mb-3">{localFileError}</p>}
                {collectionLinkError && <p className="text-xs text-red-500 mb-3">{collectionLinkError}</p>}

                <div className="flex gap-4">
                    <button type="button" onClick={handleCancel} className="flex-1 py-3 px-4 bg-gray-100 rounded-md text-gray-700 font-medium hover:bg-gray-200">
                        {t('words.cancel')}
                    </button>
                    <button
                        type="button"
                        onClick={handleConfirm}
                        disabled={!canConfirm || !!localFileError}
                        className={`flex-1 py-3 px-4 rounded-md font-medium ${canConfirm && !localFileError ? 'bg-gray-600 text-white hover:bg-gray-700' : 'bg-gray-300 text-gray-500 cursor-not-allowed'}`}
                    >
                        {t('words.done')}
                    </button>
                </div>
            </div>
        </div>
    );

    return createPortal(modalBody, document.body);
};

export default AnalysisFileModal;
